import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Check, ChevronUp, ChevronDown, User, CreditCard, FileText, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CreateInvoiceForm } from "@/components/invoice/CreateInvoiceForm";
import { UploadInvoiceForm } from "@/components/invoice/UploadInvoiceForm";
import { useQueryClient } from "@tanstack/react-query";
import { showToast } from "@/lib/toast";

type TaskIconType = 'user' | 'creditCard' | 'fileText' | 'plus';

interface OnboardingTask {
  id: string;
  titleKey: string;
  title: string;
  route: string;
  iconType: TaskIconType;
  completed: boolean;
  expandSection?: string;
}

interface StoredTask {
  id: string;
  titleKey: string;
  route: string;
  iconType: TaskIconType;
  completed: boolean;
  expandSection?: string;
}

export const ChecklistOnboarding = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [tasks, setTasks] = useState<OnboardingTask[]>([]);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const isInitialMount = useRef(true);

  const getInitialTasks = (): OnboardingTask[] => {
    return [
      {
        id: "profile",
        titleKey: "onboarding.setup_profile",
        title: t("onboarding.setup_profile", "Set up business profile"),
        route: "/account_settings",
        iconType: 'user',
        completed: false,
        expandSection: "business-profile",
      },
      {
        id: "stripe",
        titleKey: "onboarding.connect_stripe",
        title: t("onboarding.connect_stripe", "Connect Stripe Account"),
        route: "/account_settings",
        iconType: 'creditCard',
        completed: false,
        expandSection: "stripe-account",
      },
      {
        id: "template",
        titleKey: "onboarding.create_template",
        title: t("onboarding.create_template", "Create a Line Item Template"),
        route: "/templates",
        iconType: 'fileText',
        completed: false,
        expandSection: "line-item-templates",
      },
      {
        id: "invoice",
        titleKey: "onboarding.add_invoice",
        title: t("onboarding.add_invoice", "Add an invoice"),
        route: "/dashboard",
        iconType: 'plus',
        completed: false,
      },
    ];
  };

  useEffect(() => {
    if (isInitialMount.current) {
      const initialTasks = getInitialTasks();

      const savedTasks = localStorage.getItem("onboardingTasks");
      if (savedTasks) {
        try {
          const parsedTasks = JSON.parse(savedTasks) as StoredTask[];
          if (Array.isArray(parsedTasks) && parsedTasks.every(task =>
            typeof task.id === 'string' &&
            typeof task.titleKey === 'string' &&
            typeof task.route === 'string' &&
            typeof task.iconType === 'string'
          )) {
            const updatedTasks: OnboardingTask[] = parsedTasks.map(storedTask => ({
              ...storedTask,
              title: t(storedTask.titleKey, storedTask.titleKey.split('.').pop() || '')
            }));
            setTasks(updatedTasks);
          } else {
            console.warn("Invalid saved tasks format, using initial tasks");
            setTasks(initialTasks);
          }
        } catch (error) {
          console.error("Error parsing saved tasks:", error);
          setTasks(initialTasks);
        }
      } else {
        setTasks(initialTasks);
      }

      checkTasksCompletion();

      const collapsedState = localStorage.getItem("onboardingCollapsed");
      if (collapsedState) {
        setIsOpen(collapsedState !== "true");
      }

      isInitialMount.current = false;
    }
  }, [t]);

  useEffect(() => {
    setTasks(prevTasks =>
      prevTasks.map(task => ({
        ...task,
        title: t(task.titleKey, task.titleKey.split('.').pop() || '')
      }))
    );
  }, [t, i18n.language]);

  const renderTaskIcon = (iconType: TaskIconType) => {
    switch (iconType) {
      case 'user':
        return <User size={12} />;
      case 'creditCard':
        return <CreditCard size={12} />;
      case 'fileText':
        return <FileText size={12} />;
      case 'plus':
        return <Plus size={12} />;
      default:
        return <Plus size={12} />;
    }
  };

  const checkTasksCompletion = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data: profile } = await supabase
        .from('business_profiles')
        .select(`
          business_name, 
          business_email, 
          business_phone,
          business_tax_id, 
          business_iban, 
          business_swift, 
          street_address, 
          postal_code, 
          city, 
          bank_name,
          stripe_account_id, 
          stripe_charges_enabled, 
          stripe_payouts_enabled, 
          sepa_enabled
        `)
        .eq('user_id', session.user.id)
        .maybeSingle();

      const { data: templates } = await supabase
        .from('line_item_templates')
        .select('id')
        .eq('user_id', session.user.id);

      const { data: invoices } = await supabase
        .from('invoices')
        .select('id')
        .eq('user_id', session.user.id);

      setTasks(prev => {
        const newTasks = [...prev].map(task => ({ ...task }));

        const profileTaskIndex = newTasks.findIndex(task => task.id === "profile");
        if (profileTaskIndex >= 0 && profile) {
          const isProfileComplete = Boolean(
            profile.business_name &&
            profile.business_name.trim() !== '' &&
            profile.business_email &&
            profile.business_email.trim() !== '' &&
            profile.business_phone &&
            profile.business_phone.trim() !== '' &&
            profile.business_tax_id &&
            profile.business_tax_id.trim() !== '' &&
            profile.business_iban &&
            profile.business_iban.trim() !== '' &&
            profile.business_swift &&
            profile.business_swift.trim() !== '' &&
            profile.street_address &&
            profile.street_address.trim() !== '' &&
            profile.postal_code &&
            profile.postal_code.trim() !== '' &&
            profile.city &&
            profile.city.trim() !== '' &&
            profile.bank_name &&
            profile.bank_name.trim() !== ''
          );

          newTasks[profileTaskIndex].completed = isProfileComplete;
        }

        const stripeTaskIndex = newTasks.findIndex(task => task.id === "stripe");
        if (stripeTaskIndex >= 0 && profile) {
          newTasks[stripeTaskIndex].completed = Boolean(
            profile.stripe_account_id &&
            profile.stripe_charges_enabled &&
            profile.stripe_payouts_enabled &&
            profile.sepa_enabled
          );
        }

        const templateTaskIndex = newTasks.findIndex(task => task.id === "template");
        if (templateTaskIndex >= 0) {
          newTasks[templateTaskIndex].completed = templates && templates.length > 0;
        }

        const invoiceTaskIndex = newTasks.findIndex(task => task.id === "invoice");
        if (invoiceTaskIndex >= 0) {
          newTasks[invoiceTaskIndex].completed = invoices && invoices.length > 0;
        }

        const storedTasks: StoredTask[] = newTasks.map(task => ({
          id: task.id,
          titleKey: task.titleKey,
          route: task.route,
          iconType: task.iconType,
          completed: task.completed,
          expandSection: task.expandSection
        }));

        localStorage.setItem("onboardingTasks", JSON.stringify(storedTasks));

        return newTasks;
      });
    } catch (error) {
      console.error("Error checking task completion:", error);
    }
  };

  const toggleCollapse = () => {
    setIsOpen(!isOpen);
    localStorage.setItem("onboardingCollapsed", isOpen.toString());
  };

  const handleTaskClick = (route: string, expandSection?: string, taskId?: string) => {

    if (taskId === "invoice") {
      setIsInvoiceDialogOpen(true);
      return;
    }

    if (expandSection) {
      navigate(route, {
        state: {
          expandAccordion: expandSection,
          timestamp: new Date().getTime()
        }
      });
    } else {
      navigate(route);
    }
  };

  const handleSuccess = (action: "created" | "uploaded") => {
    setIsInvoiceDialogOpen(false);
    queryClient.invalidateQueries({ queryKey: ["invoices-with-schedules"] });
    showToast.success(`Invoice ${action} successfully`);
    checkTasksCompletion();
  };

  const handleInvoiceDialogOpenChange = (newOpen: boolean) => {
    setIsInvoiceDialogOpen(newOpen);
    if (newOpen) {
      setTimeout(() => {
        const dialogContent = document.querySelector('[role="dialog"]');
        if (dialogContent) {
          dialogContent.scrollTop = 0;
        }
      }, 0);
    }
  };

  const completedTasks = tasks.filter(task => task.completed).length;
  const totalTasks = tasks.length;
  const progress = Math.round((completedTasks / totalTasks) * 100);

  if (tasks.length === 0) {
    return null;
  }

  if (completedTasks === totalTasks && totalTasks > 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed bottom-6 right-6 z-50 w-72 hidden md:block"
    >
      <Dialog open={isInvoiceDialogOpen} onOpenChange={handleInvoiceDialogOpenChange}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto p-6 bg-[#F1F6F5] border-none">
          <DialogHeader>
            <DialogTitle></DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="create" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-[#BCC4F4]">
              <TabsTrigger value="create">{t("add_invoice_dialog.create_invoice", "Create Invoice")}</TabsTrigger>
              <TabsTrigger value="upload" disabled>{t("add_invoice_dialog.upload_invoice", "Upload Invoice (🔜)")}</TabsTrigger>
            </TabsList>
            <TabsContent value="create" className="mt-6">
              <CreateInvoiceForm mode="create" onSuccess={() => handleSuccess("created")} />
            </TabsContent>
            <TabsContent value="upload" className="mt-6">
              <UploadInvoiceForm onSuccess={() => handleSuccess("uploaded")} />
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <Card className="border border-[#8791F3]/20 shadow-lg">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger asChild>
            <div
              className="p-3 cursor-pointer bg-[#8791F3] text-white rounded-t-lg flex items-center justify-between"
              onClick={toggleCollapse}
            >
              <h3 className="font-bold text-sm">
                {t("onboarding.getting_started", "Getting Started")} ({completedTasks}/{totalTasks})
              </h3>
              <div className="flex items-center gap-2">
                <div className="w-20 h-1.5 bg-white/30 rounded-full">
                  <div
                    className="h-full bg-[#5CC2BB] rounded-full"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                {isOpen ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
              </div>
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="p-3 space-y-2 bg-white">
              <AnimatePresence>
                {tasks.map((task) => (
                  <motion.div
                    key={task.id}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Button
                      variant="ghost"
                      className={`w-full justify-start text-left p-2 h-auto ${task.completed
                          ? "text-[#5CC2BB] hover:text-[#5CC2BB] hover:bg-white"
                          : "text-[#34375B]/70 hover:text-[#34375B] hover:bg-[#5CC2BB]"
                        }`}
                      onClick={() => handleTaskClick(task.route, task.expandSection, task.id)}
                    >
                      <div className="flex items-center gap-2 w-full">
                        <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center border ${task.completed
                            ? "border-[#5CC2BB] bg-[#5CC2BB]/10"
                            : "border-[#34375B]/30"
                          }`}>
                          {task.completed ? <Check size={12} /> : renderTaskIcon(task.iconType)}
                        </div>
                        <span className="text-sm font-medium truncate">{task.title}</span>
                      </div>
                    </Button>
                  </motion.div>
                ))}
              </AnimatePresence>
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    </motion.div>
  );
};
